/*
 * @format
 */

import { DateTimeFormatter, ZonedDateTime, ZoneId } from '@js-joda/core';
import '@js-joda/timezone';

import moment from 'moment-timezone';

import { MINUTES_SINCE_SUNDAY, MODES } from './consts';

export const isMatchingZoneId = (x, y) => {
  if (!Array.isArray(x) || !Array.isArray(y)) {
    return false;
  }

  return x.join('_') === y.join('_');
};

export const getTypeFriendlyName = deviceType => {
  switch (deviceType) {
    case 'prt':
      return 'Heating';
    case 'si':
      return 'Halo';
    case 'boiler':
      return 'Boiler';
    default:
      return 'Device';
  }
};

export const makeModeNameHumanized = (modeName, nextEventTime = null) => {
  switch (modeName) {
    case MODES.Off:
      return 'Off';
    case MODES.Auto:
      return 'Auto';
    case MODES.Manual:
      return 'Manual';
    case MODES.OverrideNext:
      return nextEventTime ? `Override until ${nextEventTime}` : 'Override';
    case MODES.OverridePlusHours:
      return 'Plus Hours';
    case MODES.DSRActive:
      return 'DSR Program';
    default:
      return 'Unknown Mode';
  }
};

export const makeDhwModeNameHumanized = modeName => {
  switch (modeName) {
    case MODES.Off:
      return 'Off';
    case MODES.On:
      return 'On';
    case MODES.Auto:
      return 'Auto';
    case MODES.OverridePlusHours:
      return 'Boost';
    default:
      return 'Unknown Mode';
  }
};

export const humanizeTemperature = (
  setpoint,
  showdegrees,
  showcelsius,
  showOff,
  offValue,
) => {
  if (showOff) {
    const setpointFrost = offValue || 500;
    if (setpoint <= setpointFrost) {
      return 'OFF';
    }
  }

  let degreeSymbol = '';
  if (showdegrees) {
    degreeSymbol = '°';
  }
  if (showcelsius) {
    degreeSymbol += 'c';
  }
  return `${(setpoint / 100).toFixed(1)}${degreeSymbol}`;
};

// A formatted decimal such as "21.5" as it should be read aloud. Speech
// engines make their own call on a bare decimal point ("point", "dot" or the
// punctuation name, depending on voice, language and verbosity settings), so
// spell it out, and drop a zero fraction rather than have it read at all.
export const spokenDecimal = formatted => {
  const [whole, fraction] = String(formatted).split('.');
  return !fraction || /^0+$/.test(fraction)
    ? whole
    : `${whole} point ${fraction}`;
};

export const spokenTemperature = setpoint =>
  `${spokenDecimal(humanizeTemperature(setpoint))} degrees celsius`;

// A schedule temperature as read aloud. Schedules show OFF at or below the
// frost (or given minimum) setpoint, so say "Off" there rather than reading
// out five degrees; otherwise the spoken temperature.
export const spokenTemperatureOrOff = (setpoint, offValue) =>
  humanizeTemperature(setpoint, false, false, true, offValue) === 'OFF'
    ? 'Off'
    : spokenTemperature(setpoint);

// A time as read aloud, in the zone's own 12 or 24 hour form (the same format
// strings as formatTime, and the same default). Speech engines read "16:30"
// well but drop the ":00" from a whole hour, so "06:00" comes out as "six";
// whole hours are worded instead, as UK speakers say them: "6 am",
// "16 hundred", "oh 6 hundred", and "midnight" for 00:00 in 24-hour. 12-hour
// times get a space before am or pm so the suffix is read as one.
export const spokenTime = (minutesSinceMidnight, format = 'h:mma') => {
  const minutes = minutesSinceMidnight % 1440;
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  const twoDigits = n => String(n).padStart(2, '0');

  if (format.includes('a')) {
    const suffix = hour < 12 ? 'am' : 'pm';
    const hour12 = hour % 12 || 12;
    return minute
      ? `${hour12}:${twoDigits(minute)} ${suffix}`
      : `${hour12} ${suffix}`;
  }
  if (minute) {
    return `${twoDigits(hour)}:${twoDigits(minute)}`;
  }
  if (hour === 0) {
    return 'midnight';
  }
  return hour < 10 ? `oh ${hour} hundred` : `${hour} hundred`;
};

export const humanizeDhwState = on => (on ? 'ON' : 'OFF');

// convert SI time (utc) into uk time zone.
// it will auto change between gmt and bst
export const convertToSiTime = utcTime => moment(utcTime).tz('Europe/London');

export const startOfSiDay = () => {
  const zdt = ZonedDateTime.now(ZoneId.of('Europe/London'));

  const startLondon = zdt.withHour(0).withMinute(0).withSecond(0).withNano(0);

  return startLondon;
};

export const humanizeTime = (
  minutesSinceMidnight,
  format = 'HH:mm',
  startOfDay = startOfSiDay(),
) => {
  let time;
  const startLondon = startOfDay;

  // previously we tried to do this the proper way using @js-joda/locale-en, however that
  // library caused hermes to never complete compilation, and the only use we had for it
  // was to display am/pm. We tried the prebuilt en-us build which was smaller and hermes
  // did work with, but the US locale uses AM/PM instead. The not great solution for now
  // is to do it manually
  const ourFormat = format.replace('a', '');

  const minutesWithoutDays = minutesSinceMidnight % 1440;
  time = startLondon
    .withHour(Math.floor(minutesWithoutDays / 60))
    .withMinute(minutesWithoutDays % 60);

  const customSuffix =
    ourFormat !== format ? (minutesWithoutDays < 720 ? 'am' : 'pm') : '';

  const formatter = DateTimeFormatter.ofPattern(ourFormat);
  return `${time.format(formatter)}${customSuffix}`;
};

export const humanizeTimeAsComponents = (
  minutesSinceMidnight,
  is24hDisplay,
  startOfDay = startOfSiDay(),
) => {
  let time;
  const startLondon = startOfDay;

  const minutesWithoutDays = minutesSinceMidnight % 1440;
  time = startLondon
    .withHour(Math.floor(minutesWithoutDays / 60))
    .withMinute(minutesWithoutDays % 60);

  const customSuffix = minutesWithoutDays < 720 ? 'am' : 'pm';

  const hourFormatter = DateTimeFormatter.ofPattern(is24hDisplay ? 'HH' : 'h');
  const minuteFormatter = DateTimeFormatter.ofPattern('mm');
  const result = [
    time.format(hourFormatter),
    ':',
    time.format(minuteFormatter),
  ];

  if (!is24hDisplay) {
    result.push(customSuffix);
  }

  return result;
};

export const humanizePlusHours = minutes => {
  const hour = Math.floor(minutes / 60);
  const minute = `${minutes % 60}`.padStart(2, '0');
  return `${hour}:${minute}`;
};

// A length of time as read aloud: "2 hours", "1 hour 5 minutes", "45 minutes".
// humanizePlusHours draws it as "2:00", which a screen reader takes for a
// clock time.
export const spokenDuration = totalMinutes => {
  const rounded = Math.round(totalMinutes);
  const hours = Math.floor(rounded / 60);
  const minutes = rounded % 60;
  const count = (n, unit) => `${n} ${unit}${n === 1 ? '' : 's'}`;

  if (hours && minutes) {
    return `${count(hours, 'hour')} ${count(minutes, 'minute')}`;
  }
  return hours ? count(hours, 'hour') : count(minutes, 'minute');
};

// converts minutes from midnight to H:MM am/HH:mm pm
export const humanizeTimeTwelveHour = minutesSinceMidnight =>
  humanizeTime(minutesSinceMidnight, 'h:mma');

export const formatTime = (minutesSinceMidnight, format = 'h:mma') =>
  humanizeTime(minutesSinceMidnight, format);

// expose moment formatting to app
export const momentFormat = (val, oldFormat, newFormat) =>
  moment(val, oldFormat).format(newFormat);

export function timestampToDuration(timestamp) {
  if (!timestamp) {
    return 'Never';
  }
  return moment(timestamp).from();
}

export const convert24HourTimeToMinutes = (timeString, format = 'HH:mm') => {
  // we're replacing the date time library version of this function and it
  // gets very messy when daylight savings become involved.
  if (!timeString || timeString.indexOf(':') < 0) {
    return NaN;
  }

  // this function is sometimes called when in 12 hour
  // format but needs to be 24 to calculate correctly
  const formattedTimeString = momentFormat(timeString, format, 'HH:mm');

  const [hours, minutes] = formattedTimeString.split(':');

  return Number(hours) * 60 + Number(minutes.substring(0, 2));
};

export const dehumanizeTime = convert24HourTimeToMinutes;

// Date Object -> "Thursday"
export const dateToLongDayString = date => moment(date).format('dddd');

// this one does datetime object to minutes past midnight
export const dateTimeToMinutesSinceMidnight = dateTime => {
  const providedDateTime = moment(dateTime);
  // previously this used moment .startOfDay and .diff, but that is logically wrong for
  // a clock change day where there have been more or less hours than shown on clocks
  // TODO: prove that there are no cases where this needs to show more than 24 hours
  return providedDateTime.hour() * 60 + providedDateTime.minutes();
};

export const getMinutesSinceSunday = date => {
  const currentDay = dateToLongDayString(date);
  const minutesSinceMidnight = dateTimeToMinutesSinceMidnight(date);
  const minutesSinceSunday =
    MINUTES_SINCE_SUNDAY[currentDay] + minutesSinceMidnight;

  return minutesSinceSunday;
};

const daysOfWeekInOrder = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

export const getFollowingDay = dayName => {
  const index = daysOfWeekInOrder.indexOf(dayName);
  const next = (index + 1) % 7;
  return daysOfWeekInOrder[next];
};

export const getMinuteIntervals = (minMinutes, maxMinutes) => {
  const intervals = [];

  let max = maxMinutes;

  if (minMinutes > maxMinutes) {
    max = maxMinutes + 1440;
  }

  for (let minuteCount = minMinutes; minuteCount <= max; minuteCount += 15) {
    if (minuteCount > minMinutes && minuteCount < max) {
      if (minuteCount <= 1425) {
        intervals.push(minuteCount);
      }
    }
  }

  return intervals;
};

export const dateFormatter = (date, defaultValue) => {
  if (!date) {
    return defaultValue;
  }

  try {
    const formattedDate = moment(date).format('DD/MM/YYYY HH:mm');
    return formattedDate !== 'Invalid date' ? formattedDate : date;
  } catch (e) {
    console.log('Date parsing went wrong', e);
    return defaultValue;
  }
};

export const getDayOfWeek = date => {
  const day = date.getDay();

  const currentDay = daysOfWeekInOrder[day];

  if (!currentDay) {
    throw Error('The day entered was not recognised');
  }

  return currentDay;
};

export const getPreviousDay = dayName => {
  const index = daysOfWeekInOrder.indexOf(dayName);

  const prevIndex = (index + 6) % 7;

  return daysOfWeekInOrder[prevIndex];
};

export const zoneSort = (a, b) => {
  const prtDiff = a.zoneId[0].localeCompare(b.zoneId[0]);
  if (!prtDiff) {
    return prtDiff;
  }
  return a.zoneId[1] - b.zoneId[1];
};

export const sortHomesById = (a, b) => {
  return a.id > b.id ? 1 : -1;
};

export const uppercaseName = name =>
  name.charAt(0).toUpperCase() + name.slice(1);

export const getFullName = user => {
  if (!user.firstName || !user.lastName) {
    if (user.firstName) {
      return user.firstName.trim();
    }
    return user.email;
  }

  const firstname = uppercaseName(user.firstName).trim();
  const lastname = uppercaseName(user.lastName).trim();

  return `${firstname} ${lastname}`;
};

const swapArrayElements = (arr, a, b) => {
  const newArray = [...arr];
  const temp = newArray[a];
  newArray[a] = newArray[b];
  newArray[b] = temp;
  return newArray;
};

export const getSettingsDifferences = (orginalSettings, newSettings) => {
  if (orginalSettings) {
    let result = [];
    if (newSettings) {
      if (
        orginalSettings.optimisedStartEnabled !==
        newSettings.optimisedStartEnabled
      ) {
        result.push({
          optimisedStartEnabled: newSettings.optimisedStartEnabled,
        });
      }

      if (
        orginalSettings.optimisedStopEndabled !==
        newSettings.optimisedStopEndabled
      ) {
        result.push({
          optimisedStopEndabled: newSettings.optimisedStopEndabled,
        });
      }

      if (
        orginalSettings.delayedStartEnabled !== newSettings.delayedStartEnabled
      ) {
        result.push({
          delayedStartEnabled: newSettings.delayedStartEnabled,
        });
      }

      if (orginalSettings.childLockOn !== newSettings.childLockOn) {
        result.push({
          childLockOn: newSettings.childLockOn,
        });
      }

      if (
        orginalSettings.frostProtectionSetpoint !==
        newSettings.frostProtectionSetpoint
      ) {
        result.push({
          frostProtectionSetpoint: newSettings.frostProtectionSetpoint,
        });
      }

      if (orginalSettings.temperatureOffset !== newSettings.temperatureOffset) {
        result.push({
          temperatureOffset: newSettings.temperatureOffset,
        });
      }

      if (
        orginalSettings.displayToleranceDot1 !==
        newSettings.displayToleranceDot1
      ) {
        result.push({
          displayToleranceDot1: newSettings.displayToleranceDot1,
        });
      }

      if (orginalSettings.timeFormat !== newSettings.timeFormat) {
        result.push({
          timeFormat: newSettings.timeFormat,
        });
      }
    }
    if (newSettings.optimisedStartEnabled) {
      if (orginalSettings.delayedStartEnabled) {
        const indexOfOptimisedStartEnabled = result
          .map(item => item.optimisedStartEnabled)
          .indexOf(true);

        const indexOfDelayedStartEnabled = result
          .map(item => item.delayedStartEnabled)
          .indexOf(false);

        result = swapArrayElements(
          result,
          indexOfOptimisedStartEnabled,
          indexOfDelayedStartEnabled,
        );
      }

      if (newSettings.delayedStartEnabled) {
        if (orginalSettings.optimisedStartEnabled) {
          const indexOfOptimisedStartEnabled = result
            .map(item => item.optimisedStartEnabled)
            .indexOf(false);

          const indexOfDelayedStartEnabled = result
            .map(item => item.delayedStartEnabled)
            .indexOf(true);

          result = swapArrayElements(
            result,
            indexOfOptimisedStartEnabled,
            indexOfDelayedStartEnabled,
          );
        }
      }
    }
    return result;
  }
  return newSettings;
};

export const getSettingFriendlyName = setting => {
  let result = '';
  switch (setting) {
    case 'optimisedStartEnabled':
      result = 'Optimised Start Enabled';
      break;
    case 'optimisedStopEndabled':
      result = 'Optimised Stop Enabled';
      break;
    case 'delayedStartEnabled':
      result = 'Delayed Start Enabled';
      break;
    case 'childLockOn':
      result = 'Child Lock On';
      break;
    case 'temperatureOffset':
      result = 'Temperature Offset';
      break;
    case 'timeFormat':
      result = 'Time Format';
      break;
    case 'frostProtectionSetpoint':
      result = 'Frost Protection Setpoint';
      break;
    default:
      break;
  }
  return result;
};

export const IsZoneInHolidayMode = zone => {
  return zone.holidayMode === 3;
};

/* =======
  Returns on object to destruct with the appropriate field
  for dhw and ch schedule events. See example

  const event = {
    startTime: MINUTES_SINCE_SUNDAY[s.day] + p.startTime,
    ...getScheduleEventAction(p),
  }
======= */
export const getScheduleEventAction = event => {
  if (event?.on !== undefined) {
    return {
      on: event.on,
    };
  }

  return {
    setpoint: event.setpoint,
  };
};

export const isHpScheduleEvent = event => {
  if (event) {
    return event.setpoint === 'comfort' || event.setpoint === 'reduced';
  }

  return false;
};

export const sanitisedHpEventSetpoint = setpoint => {
  const setpointLookup = { comfort: 'Comfort', reduced: 'Setback' };

  if (setpoint && typeof setpoint === 'string') {
    const loweredSetpoint = setpoint.toLowerCase();
    const validHpEventSetpoints = Object.keys(setpointLookup);

    if (validHpEventSetpoints.includes(loweredSetpoint)) {
      return setpointLookup[loweredSetpoint];
    }
  }

  return 'Invalid';
};

const zeroDuration = { hours: 0, minutes: 0, seconds: 0 };
// Time Durations in the format of "03:00:00" - hrs, mins, seconds
export function splitTimeDuration(timeDuration) {
  if (typeof timeDuration !== 'string') {
    return zeroDuration;
  }

  const parts = timeDuration.trim().split(':');
  if (parts.length !== 3) {
    console.warn('TimeDuration not in expected format', timeDuration);
    return zeroDuration;
  }

  return {
    hours: Number(parts[0]),
    minutes: Number(parts[1]),
    seconds: Number(parts[2]),
  };
}

export function buildTimeDuration(hours, minutes, seconds) {
  return `${String(hours).padStart(2, 0)}:${String(minutes).padStart(
    2,
    0,
  )}:${String(seconds).padStart(2, 0)}`;
}

/** Anything worse than or equal to this will show 0 bars. */
const MIN_RSSI = -100;

/** Anything better than or equal to this will show the max bars. */
const MAX_RSSI = -55;

export const wifiSignalToBarCount = (rssi, numLevels = 4) => {
  if (rssi <= MIN_RSSI) {
    return 0;
  } else if (rssi >= MAX_RSSI) {
    return numLevels;
  }
  const inputRange = MAX_RSSI - MIN_RSSI;
  const outputRange = numLevels;

  const signalStrengthBarCount = ((rssi - MIN_RSSI) * outputRange) / inputRange;

  return Math.round(signalStrengthBarCount);
};

export const wifiSignalText = signalStrengthBarCount => {
  const barCountToText = {
    0: 'Unknown',
    1: 'Low',
    2: 'Ok',
    3: 'Good',
    4: 'Very Good',
  };

  return barCountToText[signalStrengthBarCount];
};
